import React, { useRef, memo } from 'react';
import { toast } from 'react-toastify';
import { FiClipboard } from 'react-icons/fi'; // Clipboard icon
import './DeviceCard.css';
import { PRODUCT_NAMES, SYSTEM_TYPES } from './constants';
// Movement tracker no longer uses asset registry; simple textual display.
import { getSystemTypeForCore } from './utils/systemTypeCache';

const DeviceCard = ({ device, selected, onSelect }) => {
  const toastIdRef = useRef(null);

  // Handle card selection
  const handleSelect = () => {
    if (onSelect) {
      onSelect();
    }
  };

  // Determine the class to apply based on the status in event data
  const getStatusClass = (aggregatedData) => {
    return aggregatedData["k/status"] !== "offline" ? "online" : "offline";
  };

  const getLastHeard = (time) => {
    if (time && !isNaN(new Date(time).getTime())) {
      return `Last heard: ${formatDate(time)}`;
    } else {
      return "";
    }
  };

  const getProductName = (productId) => PRODUCT_NAMES[Number(productId)] || 'Unknown';

  // Utility function to format the published_at date
  const formatDate = (publishedAt) => {
    const currentDate = new Date();
    const publishedDate = new Date(publishedAt);

    const sameDay = currentDate.toDateString() === publishedDate.toDateString();
    const sameYear = currentDate.getFullYear() === publishedDate.getFullYear();

    // Format the time (HH:MM)
    const timeString = publishedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Return only time if the same day
    if (sameDay) {
      return publishedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }

    // Return day and time if same year
    if (sameYear) {
      return `${publishedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${timeString}`;
    }

    // Return full date and time if different year
    return `${publishedDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}, ${timeString}`;
  };

  const getSystemType = (systemTypeId) => {
    const parsed = parseInt(systemTypeId, 10);
    if (!isNaN(parsed) && SYSTEM_TYPES[parsed]) {
      return SYSTEM_TYPES[parsed];
    }
    const cached = getSystemTypeForCore(device.coreid);

    if (cached) {
      //console.log(`coreid ${device.coreid} parsed ${parsed} cached ${cached}`);
      return SYSTEM_TYPES[cached] || 'Unknown';
    } else {
      return 'Unknown';
    }
  };

  // Format movement summary for new movementTracker state
  function movementSummary(movement, systemTypeLabel) {
    if (!movement) return <div>No movement</div>;
    const isCloudBed = systemTypeLabel.includes('Cloud Bed');
    const isOfficeOrCloset = systemTypeLabel.includes('Office') || systemTypeLabel.includes('Closet');
    const mainLabel = isCloudBed ? 'bed' : 'main';
    const bedLabel = isCloudBed ? 'bed legs' : 'bed';

    let mainCountsStr;
    if (isCloudBed) {
      // Only show up (closet) then down (couch) counts; omit bed/manual
      const upCount = movement.main.counts['closet'] ?? 0;
      const downCount = movement.main.counts['couch'] ?? 0;
      mainCountsStr = `up:${upCount}  down:${downCount}`;
    } else if (isOfficeOrCloset) {
      // Only show open (closet) then closed (couch) counts; omit bed/manual
      const openCount = movement.main.counts['closet'] ?? 0;
      const closedCount = movement.main.counts['couch'] ?? 0;
      const manualCount = movement.main.counts['manual'] ?? 0;
      mainCountsStr = `open:${openCount}  close:${closedCount}  manual:${manualCount}`;
    } else {
      mainCountsStr = Object.entries(movement.main.counts)
        .filter(([k]) => k !== 'unknown')
        .map(([k, v]) => `${k}:${v}`)
        .join(' ');
    }

    // Cloud Bed leg label inversion logic
    const mapBedCurrentDisplay = (val) => {
      if (val === 'unknown') return '???';
      if (!isCloudBed) return val;
      if (val === 'in') return 'out';
      if (val === 'out') return 'in';
      return val; // middle or unknown
    };
    const mapBedMovingDisplay = (dir) => {
      return mapBedCurrentDisplay(dir);
    };
    const mapMainCurrentDisplay = (val) => {
      if (val === 'unknown') return '???';
      if (!isCloudBed && !isOfficeOrCloset) return val;
      if (isCloudBed) {
        if (val === 'couch') return 'down';
        if (val === 'closet') return 'up';
      } else {
        if (val === 'couch') return 'closed';
        if (val === 'closet') return 'open';
      }
      return val;
    };

    return (
      <div>
        <div>{mainLabel}: {mapMainCurrentDisplay(movement.main.current)}{movement.main.moving ? ` (moving ${movement.main.moving})` : ''}</div>
        {movement.hasBed && movement.bed && (
          <>
            <div>{bedLabel}: {mapBedCurrentDisplay(movement.bed.current)}{movement.bed.moving ? ` (moving ${mapBedMovingDisplay(movement.bed.moving)})` : ''}</div>
          </>
        )}
        <div>{mainLabel} counts: {mainCountsStr}</div>
      </div>
    );
  }

  // Function to handle copying the device ID to clipboard
  const copyToClipboard = (event) => {
    event.stopPropagation(); // Prevents the card from expanding when clicking the clipboard icon

    navigator.clipboard.writeText(device.coreid)
      .then(() => {
        // Check if a toast is already active, to prevent duplicate toasts
        if (!toast.isActive(toastIdRef.current)) {
          toastIdRef.current = toast.success("Device ID copied to clipboard!", {
            className: "custom-toast-success",
            icon: <FiClipboard className="toast-icon" />,
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            onClose: () => {
              // When the toast is closed, clear the ref
              toastIdRef.current = null;
            },
          });
        }
      })
      .catch((error) => {
        toast.error("Failed to copy device ID.", {
          className: "custom-toast-error",
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      });
  };

  const isStale = false; // Stale indicator currently disabled.

  // Render movement status icons for the right column
  const renderMovementIcons = () => {
    if (!device.movement) return null;

    const systemTypeLabel = getSystemType(device.aggregatedData["r/system_type"]);
    const isCloudBed = systemTypeLabel.includes('Cloud Bed');
    const isOfficeOrCloset = systemTypeLabel.includes('Office') || systemTypeLabel.includes('Closet');
    const mainLabel = isCloudBed ? 'bed' : 'main';
    const bedLabel = isCloudBed ? 'bed legs' : 'bed';

    // Map positions for display
    const mapMainPosition = (val) => {
      if (val === 'unknown') return '???';
      if (!isCloudBed && !isOfficeOrCloset) return val;
      if (isCloudBed) {
        if (val === 'couch') return 'down';
        if (val === 'closet') return 'up';
      } else {
        if (val === 'couch') return 'closed';
        if (val === 'closet') return 'open';
      }
      return val;
    };

    const mapBedPosition = (val) => {
      if (val === 'unknown') return '???';
      if (!isCloudBed) return val;
      if (val === 'in') return 'out';
      if (val === 'out') return 'in';
      return val;
    };

    const mainPosition = mapMainPosition(device.movement.main.current);
    const mainMoving = device.movement.main.moving;
    const bedPosition = device.movement.hasBed && device.movement.bed 
      ? mapBedPosition(device.movement.bed.current) 
      : null;
    const bedMoving = device.movement.hasBed && device.movement.bed 
      ? device.movement.bed.moving 
      : null;

    // Arrow direction icons
    /*const getArrowIcon = (position, moving) => {
      if (moving) {
        return moving === 'up' || moving === 'out' ? '↑' : '↓';
      }
      return null;
    };*/

    return (
      <div className="movement-status-icons">
        {/* Main movement status */}
        <div className="status-icon-row">
          <span className="status-icon-label">{mainLabel}:</span>
          <div className={`status-icon ${mainMoving ? 'moving' : 'active'}`}>
            {mainMoving ? (
              <span className="arrow-icon">{/*getArrowIcon(mainPosition, mainMoving)*/mainMoving}</span>
            ) : (
              <span className="position-badge">{mainPosition}</span>
            )}
          </div>
        </div>

        {/* Bed movement status */}
        {device.movement.hasBed && bedPosition && (
          <div className="status-icon-row">
            <span className="status-icon-label">{bedLabel}:</span>
            <div className={`status-icon ${bedMoving ? 'moving' : 'active'}`}>
              {bedMoving ? (
                <span className="arrow-icon">{/*getArrowIcon(bedPosition, bedMoving)*/bedMoving}</span>
              ) : (
                <span className="position-badge">{bedPosition}</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`device-card ${isStale ? 'stale' : ''} ${selected ? 'selected' : ''}`}>
      <div className="device-header" onClick={handleSelect}>
        <div className="device-header-left">
          <div className={`device-online-indicator ${getStatusClass(device.aggregatedData)}`}></div>
          <div className="device-info">
            <h4>{getSystemType(device.aggregatedData["r/system_type"])}</h4>
            <div className="device-id-container">
              <p>{device.coreid}</p>
              <FiClipboard className="clipboard-icon" onClick={copyToClipboard} title="Copy Device ID" />
            </div>
            <p>{getProductName(device.productid)} - fw_v{device.fw_version}</p>
            <p>{getLastHeard(device.published_at)} {isStale && <span style={{ color: '#b76', marginLeft: '0.5ch' }}>(stale)</span>}</p>
            {device.movement && (
              <div className="movement-tracker-summary" style={{ marginTop: '0.5rem', fontSize: '0.7rem', opacity: 0.9, lineHeight: '1.2' }}>
                {movementSummary(device.movement, getSystemType(device.aggregatedData["r/system_type"]))}
              </div>
            )}
          </div>
        </div>
        
        <div className="device-header-right">
          {renderMovementIcons()}
        </div>
      </div>
    </div>
  );
};

// Simple memoization to avoid re-renders if core fields unchanged
const DeviceCardMemo = memo(DeviceCard, (prev, next) => {
  const a = prev.device; const b = next.device;
  if (a === b && prev.selected === next.selected) return true;
  if (prev.selected !== next.selected) return false;
  if (a.coreid !== b.coreid) return false;
  if (a.fw_version !== b.fw_version) return false;
  if (a.productid !== b.productid) return false;
  if (a.published_at !== b.published_at) return false;
  const aKeys = Object.keys(a.aggregatedData); const bKeys = Object.keys(b.aggregatedData);
  if (aKeys.length !== bKeys.length) return false;
  for (let k of bKeys) { if (a.aggregatedData[k] !== b.aggregatedData[k]) return false; }
  if ((a.movement?.state) !== (b.movement?.state)) return false;
  return true;
});

export default DeviceCardMemo;
